/* eslint-disable no-case-declarations */
import React, { useCallback, useContext, useEffect, useMemo } from 'react';

import { Report67Skeleton, Report68Skeleton, Report69Skeleton, Report70Skeleton, Report71Skeleton, Report72Skeleton, Report73Skeleton, Report74Skeleton } from 'pages/reports/components/ReportSkeleton/ReportSkeleton';
import { ReportToolTip } from 'pages/reports/components/StudentMetricsDisplay/StudentMetricsDisplay';
import { FilterWrapper } from 'pages/reports/components/FilterWrapper/FilterWrapper';
import { getStartEndDateDefaultDays } from 'pages/reports/utils/reportDateDefaults';
import { ReportsDefaultContext } from 'pages/reports/context/ReportsDefaultContext';
import { Card, Col, Container, FormLabel, Offcanvas, Row } from 'react-bootstrap';
import { KaplanReport } from 'pages/reports/components/KaplanReport/KaplanReport';
import { StudentGroupApi, StudentGroupWithDetails } from 'apis/StudentGroupApi';
import { ComponentWrapper } from 'components/ComponentWrapper/ComponentWrapper';
import { Option, Report, ReportApi, ReportFilter } from 'apis/ReportApi';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { Report33 } from 'pages/reports/components/Report33/Report33';
import { Report60 } from 'pages/reports/components/Report60/Report60';
import { Report62 } from 'pages/reports/components/Report62/Report62';
import { Report67 } from 'pages/reports/components/Report67/Report67';
import { Report68 } from 'pages/reports/components/Report68/Report68';
import { Report69 } from 'pages/reports/components/Report69/Report69';
import { Report70 } from 'pages/reports/components/Report70/Report70';
import { Report71 } from 'pages/reports/components/Report71/Report71';
import { Report72 } from 'pages/reports/components/Report72/Report72';
import { Report73 } from 'pages/reports/components/Report73/Report73';
import { Report74 } from 'pages/reports/components/Report74/Report74';
import { Report75 } from 'pages/reports/components/Report75/Report75';
import { Transaction, TransactionApi } from 'apis/TransactionApi';
import { getIdFromURL, getStringFromURL } from 'utils/other';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { useSearchParams } from 'react-router-dom';
import { Partner } from 'apis/PartnerApi';

import 'components/GenerateReport/generate-report.scss';

import { Student } from 'apis/StudentApi';
import { getFormattedDate } from 'utils';
import { useSuperState } from 'hooks';

interface GenerateReportProps {
    autoRunReport?: boolean;
    cultureId?: number;
    highestRole?: { Id: number; Name: string };
    isSubReport?: boolean;
    onReportLoaded?: (report: Report) => void;
    propCustomFilter?: CustomFilter;
    removeFilters?: string[];
    reportId: number;
    scopeId: number;
    scopeObjectId: number;
    showNoFilters?: boolean;
    showStartEndDateOnly?: boolean;
    studentProfileId?: number;
    testType?: number; // Needed for N2K/Kaplan
    urlParamsOverride?: string;
    urlSync?: boolean;
}

interface CustomFilter {
    filterId: number;
    valueId: number;
}

const MULTI_VALUE_LABELS: Record<string, string> = {
    'AssignmentSearch': 'assignments',
    'PartnerSearch': 'partners',
    'StudentGroupsSearch': 'classrooms',
    'StudentSearch': 'students',
    'TransactionSearch': 'access codes'
};

const SINCE_START_THRESHOLD_DAYS = 3650;
const EMPTY_DISPLAY = '----';
const REPORTS_WITH_MODE_FILTER = [71, 72, 73, 74];

type ReportMode = 'Testing' | 'Training';

export function GenerateReport({
    autoRunReport = true,
    cultureId = 1,
    highestRole,
    isSubReport = false,
    onReportLoaded,
    propCustomFilter,
    removeFilters,
    reportId,
    scopeId,
    scopeObjectId,
    showNoFilters = false,
    showStartEndDateOnly = false,
    studentProfileId,
    testType = 0,
    urlParamsOverride,
    urlSync = false
}: GenerateReportProps) {

    // When `urlParamsOverride` is supplied, filter/scope params are read from it
    // (a URL or query string) rather than the browser location. Values are
    // lowercased to match `getStringFromURL`, which lowercases the whole href.
    const overrideParams = useMemo(() => {

        if (!urlParamsOverride) return null;
        const qIndex = urlParamsOverride.indexOf('?');
        const query = qIndex >= 0 ? urlParamsOverride.slice(qIndex + 1) : urlParamsOverride;
        return new URLSearchParams(query.toLowerCase());

    }, [urlParamsOverride]);

    const readUrlString = (name: string) => {

        if (!overrideParams) return getStringFromURL(name);

        // Fall back to a normalized key (non-alphanumerics stripped) so filter names
        // like "Student Id" match row/URL params keyed as "studentid".
        return overrideParams.get(name.toLowerCase())
            ?? overrideParams.get(name.toLowerCase().replace(/[^a-z0-9]/g, ''))
            ?? undefined;

    };
    const readUrlIds = (name: string) => {

        if (!overrideParams) return getIdFromURL(name);
        const raw = readUrlString(name);
        return raw !== null && raw !== undefined ? raw.split(',').map(v => parseInt(v)) : undefined;

    };

    const urlMode = readUrlString('Mode') ?? 'testing';
    const [state, setState] = useSuperState({
        currentAccessCodeList: [] as Transaction[],
        currentClassroomList: [] as StudentGroupWithDetails[],
        currentPartnerList: [] as Partner[],
        currentStudentList: [] as Student[],
        loadingReport: true,
        mode: (urlMode.toLowerCase() === 'training' ? 'Training' : 'Testing') as ReportMode,
        report: undefined as Report | undefined,
        reportFilters: undefined as ReportFilter[] | undefined,
        runOnce: true,
        show: false
    });
    const {
        currentAccessCodeList, currentClassroomList, currentPartnerList, currentStudentList,
        loadingReport, mode, report, reportFilters, runOnce, show
    } = state;

    // --- Context & refs ---
    const { formatMessage } = useIntl();
    const { createToast } = useNotification();
    const { filterOptionsCache } = useContext(ReportsDefaultContext);

    // --- Stable callbacks for child components ---
    const handleSetReport = useCallback((r: Report) => setState({ report: r }), []);
    const handleSetReportFilters = useCallback((filters: ReportFilter[] | undefined) => {

        setState({ reportFilters: filters });

    }, []);
    const handleSetLoadingReport = useCallback((loading: boolean) => setState({ loadingReport: loading }), []);

    const handleClose = () => setState({ show: false });
    const handleShow = () => setState({ show: true });

    const intl = useIntl();

    const handleSetMode = (newMode: string) => {

        setState({ mode: newMode as ReportMode });
        if (urlSync) {

            const url = new URL(window.location.href);
            url.searchParams.set('Mode', newMode);
            window.history.pushState(null, '', url.toString());

        }

    };

    // ----------------------------------------------
    //  URL param parsing
    // ----------------------------------------------

    const [searchParams] = useSearchParams();
    const urlTestType = overrideParams ? overrideParams.get('testtype') : searchParams.get('testType');
    const testTypeId = urlTestType ?? (testType ? String(testType) : null);

    // applyUrlParams: URL query values are deep-link inputs for the INITIAL load only.
    // Re-applying them on every run stomps the user's freshly chosen filter values
    // (stale StartEndDate from before midnight = blank reports, filters that snap back).
    const handleParamsInUrl = async (reportData: Report, applyUrlParams = true) => {

        let stopLoopForErrorMessage = false;
        let filtersFilled = 0;
        let requiredFilters = 0;

        // The StartEndDate window always defaults to a fixed day count on first
        // load: "Since Start" (SINCE_START_DAYS) for normal reports, or a shorter
        // window for the few reports listed in reportDateDefaults. This wins over
        // any backend-seeded value so the run matches the "Since Start" default
        // shown in the filter UI.
        const forcedDefaultDays = getStartEndDateDefaultDays(reportData.Id);

        for (const filter of reportData.ReportFilters) {

            if (filter.FilterRequired && filter.DisplayName !== 'Culture') requiredFilters++;

            // Fall back to DisplayName: some filters are keyed by display (e.g. "Student")
            // while the injected row/URL param uses that name rather than filter.Name.
            let urlFilterValue = applyUrlParams
                ? (readUrlString(filter.Name) ?? (filter.DisplayName ? readUrlString(filter.DisplayName) : undefined))?.toLowerCase()
                : undefined;
            let validOption = false;

            if (urlFilterValue) {

                switch (filter.FieldType) {

                    case 'DatePicker': {

                        if (urlFilterValue === 'week' || urlFilterValue === 'month') {

                            filter.DefaultValue = urlFilterValue;
                            const startDate = new Date();
                            const endDate = new Date();
                            startDate.setDate(startDate.getDate() - (urlFilterValue === 'week' ? 7 : 30));
                            endDate.setDate(endDate.getDate() + 1);
                            urlFilterValue = `${getFormattedDate(startDate)}|${getFormattedDate(endDate)}`;

                        }
                        const pipe = urlFilterValue.split('|');
                        if (pipe.length !== 2 || isNaN(new Date(pipe[0]).getTime()) || isNaN(new Date(pipe[1]).getTime()))
                            stopLoopForErrorMessage = true;
                        if (typeof filter.DefaultValue === 'string') filter.DefaultValue = filter.DefaultValue.toLowerCase();
                        if (filter.FilterRequired && filter.DisplayName !== 'Culture') filtersFilled++;
                        break;

                    }
                    case 'DropDownList':
                        if (urlFilterValue === 'all') {

                            const allValues = filter.FilterOptions?.map(o => o.Value).join(',');
                            if (allValues) {

                                urlFilterValue = allValues;
                                validOption = true;

                            } else {

                                // Child filter FilterOptions not pre-loaded, fetch via parent value
                                const parentFilter = reportData.ReportFilters?.find(p => p.Id === filter.ParentFilterId);
                                if (parentFilter?.Value && filter.ParentFilterId && filter.ParentFilterId > 0) {

                                    const parentValues = parentFilter.Value.split(',').map((v: string) => v.trim()).filter(Boolean);
                                    const uncached = parentValues.filter(pv => !filterOptionsCache.has(`${reportId}_${scopeObjectId}_${filter.Id}_${pv}`));
                                    if (uncached.length > 0) {

                                        const optionsByParent = await ReportApi
                                            .GetFilterOptions(filter, uncached.join(','), scopeId, scopeObjectId)
                                            .catch(() => undefined);
                                        if (optionsByParent) {

                                            Object.entries(optionsByParent).forEach(([pv, opts]) => {

                                                filterOptionsCache.set(`${reportId}_${scopeObjectId}_${filter.Id}_${pv}`, (opts as Option[]) ?? []);

                                            });

                                        }

                                    }
                                    const merged: Option[] = [];
                                    parentValues.forEach((pv: string) => {

                                        const opts = filterOptionsCache.get(`${reportId}_${scopeObjectId}_${filter.Id}_${pv}`) ?? [];
                                        opts.forEach((o: Option) => {

                                            if (!merged.some(x => x.Value === o.Value)) merged.push(o);

                                        });

                                    });
                                    if (merged.length > 0) {

                                        urlFilterValue = merged.map(o => o.Value).join(',');
                                        filter.FilterOptions = merged;
                                        validOption = true;

                                    } else {

                                        stopLoopForErrorMessage = true;

                                    }

                                } else {

                                    stopLoopForErrorMessage = true;

                                }

                            }

                        } else if (filter.FilterOptions?.some(option => option.Value === urlFilterValue)) {

                            validOption = true;

                        } else {

                            const parentFilter = reportData.ReportFilters?.find(p => p.Id === filter.ParentFilterId);
                            if (parentFilter?.Value && parentFilter.Value.length) {

                                const cacheKey = `${reportId}_${scopeObjectId}_${filter.Id}_${parentFilter.Value}`;
                                const cached = filterOptionsCache.get(cacheKey);
                                let options: Option[] | undefined = cached;
                                if (!options) {

                                    const optionsByParent = await ReportApi
                                        .GetFilterOptions(filter, parentFilter.Value, scopeId, scopeObjectId)
                                        .catch(() => {

                                            createToast(formatMessage({ id: 'unable_to_run_report.125072a9', defaultMessage: 'Unable to run Report' }), 'danger');
                                            setState({ loadingReport: false, report: { ...reportData, HasResults: false } });
                                            return undefined;

                                        });
                                    options = optionsByParent?.[parentFilter.Value];
                                    if (options) filterOptionsCache.set(cacheKey, options);

                                }
                                if (options?.some(option => option.Value === urlFilterValue)) validOption = true;
                                if (options) filter.FilterOptions = options;

                            }

                        }
                        if (!validOption) stopLoopForErrorMessage = true;
                        if (filter.FilterRequired && filter.DisplayName !== 'Culture') filtersFilled++;
                        break;
                    case 'StudentGroupsSearch':
                        if (urlFilterValue === 'all') {

                            const allClassrooms = await StudentGroupApi.GetAll('Partner', -1, scopeObjectId).catch(() => undefined);
                            const activeClassrooms = allClassrooms?.filter(c => c.Archived === false) ?? [];
                            setState({ currentClassroomList: allClassrooms || [] });
                            if (activeClassrooms.length > 0) {

                                urlFilterValue = activeClassrooms.map(c => String(c.Id)).join(',');
                                validOption = true;

                            } else {

                                stopLoopForErrorMessage = true;

                            }

                        } else {

                            validOption = await checkStudentGroups(urlFilterValue);

                        }
                        if (!validOption) stopLoopForErrorMessage = true;
                        if (filter.FilterRequired && filter.DisplayName !== 'Culture') filtersFilled++;
                        break;
                    case 'TransactionSearch':
                        validOption = await checkTransactions(urlFilterValue);
                        if (!validOption) stopLoopForErrorMessage = true;
                        if (filter.FilterRequired && filter.DisplayName !== 'Culture') filtersFilled++;
                        break;
                    case 'StudentSearch':
                        validOption = /^[a-z0-9]+$/i.test(urlFilterValue);
                        if (!validOption) stopLoopForErrorMessage = true;
                        if (filter.FilterRequired && filter.DisplayName !== 'Culture') filtersFilled++;
                        break;
                    case 'AssignmentSearch':
                        validOption = /^[a-z0-9]+$/i.test(urlFilterValue);
                        if (!validOption) stopLoopForErrorMessage = true;
                        if (filter.FilterRequired && filter.DisplayName !== 'Culture') filtersFilled++;
                        break;
                    default:
                        if (filter.FilterRequired && filter.DisplayName !== 'Culture') filtersFilled++;

                }
                filter.Value = urlFilterValue;

            } else {

                // Seed the StartEndDate window from its default day count when the
                // user hasn't supplied a range via the URL, ignoring any
                // backend-seeded value.
                if (filter.FieldType === 'DatePicker' && filter.Name === 'StartEndDate') {

                    // On re-runs keep the user's chosen range; only seed the default on initial load.
                    if (!applyUrlParams && filter.Value) {

                        if (filter.FilterRequired && filter.DisplayName !== 'Culture') filtersFilled++;
                        continue;

                    }

                    const endDate = new Date();
                    const startDate = new Date();
                    startDate.setDate(startDate.getDate() - forcedDefaultDays);
                    filter.Value = `${getFormattedDate(startDate)}|${getFormattedDate(endDate)}`;
                    if (filter.FilterRequired && filter.DisplayName !== 'Culture') filtersFilled++;
                    if (urlSync) {

                        const url = new URL(window.location.href);
                        url.searchParams.set(filter.Name, filter.Value);
                        window.history.pushState(null, '', url.toString());

                    }

                } else {

                    const defaultValue = filter.Value ?? filter.DefaultValue;
                    if (filter.FilterRequired && defaultValue) {

                        filter.Value = defaultValue;
                        filtersFilled++;
                        if (urlSync) {

                            const url = new URL(window.location.href);
                            url.searchParams.set(filter.Name, defaultValue);
                            window.history.pushState(null, '', url.toString());

                        }

                    }

                }

            }

        }

        // Mirror the filter panel's ReportDropdownList, which sets an optional child to "Pick One" ('0')
        // while its parent is unset, so the auto-run sends the same filters as a manual run.
        if (!showNoFilters && !showStartEndDateOnly && reportData.Id !== 60) {

            reportData.ReportFilters.forEach(filter => {

                if (filter.FieldType !== 'DropDownList' || !(filter.ParentFilterId > 0) || filter.FilterRequired || filter.Value) return;
                const parentFilter = reportData.ReportFilters.find(p => p.Id === filter.ParentFilterId);
                if (!parentFilter?.Value || parentFilter.Value === '0') filter.Value = '0';

            });

        }

        setState({ reportFilters: reportData.ReportFilters });
        return { filtersFilled, requiredFilters };

    };

    // ----------------------------------------------
    //  Validation helpers (for URL filter values)
    // ----------------------------------------------

    const checkStudentGroups = async (csv: string) => {

        const ids = csv.split(',').map(Number);
        const classroomList = await StudentGroupApi.GetAll('Partner', -1, scopeObjectId).catch(() => undefined);
        setState({ currentClassroomList: classroomList || [] });
        return ids.every(id => classroomList?.some(s => s.Id === id));

    };

    const checkTransactions = async (csv: string) => {

        const ids = csv.split(',').map(Number);
        const transactions = await TransactionApi.GetAll().catch(() => undefined);
        setState({ currentAccessCodeList: transactions || [] });
        return ids.every(id => transactions?.some(t => t.Id === id));

    };

    // ----------------------------------------------
    //  Report loading & execution
    // ----------------------------------------------
    const loadReports = async () => {

        let reportResult: Report | undefined;
        let parsedFilters: { filtersFilled: number; requiredFilters: number } | undefined;
        try {

            setState({ loadingReport: true });
            reportResult = await ReportApi.GetReportLayout(reportId, scopeObjectId, cultureId, autoRunReport);
            if (propCustomFilter) setCustomFilterValue(reportResult, propCustomFilter);

            const urlReportId = readUrlIds('reportId') ?? [reportId];
            const urlScopeObjectId = readUrlIds('scopeObjectId') ?? [scopeObjectId];

            if (urlReportId && urlScopeObjectId) {

                setState({ report: reportResult });
                parsedFilters = await handleParamsInUrl(reportResult);
                if (onReportLoaded && reportResult) onReportLoaded(reportResult);

            }

        } catch (error) {

            console.error('Failed to get report layout:', error);
            setState({ loadingReport: false });
            createToast(formatMessage({ id: 'unable_to_get_report_layout.a9f94bf9', defaultMessage: 'Unable to get Report Layout' }), 'danger');

        } finally {

            if (autoRunReport) {

                await runActualReport(true, reportResult, parsedFilters);

            } else {

                setState({ loadingReport: false, runOnce: false });

            }

        }

    };

    // Report runs only on initial load and when user explicitly triggers via FilterWrapper.
    const runActualReport = async (
        override = false,
        reportInput?: Report,
        preparsedFilters?: { filtersFilled: number; requiredFilters: number }
    ) => {

        const targetReport = reportInput ?? report;
        if (!targetReport) {

            setState({ loadingReport: false });
            return;

        }

        const { filtersFilled, requiredFilters } = preparsedFilters ?? await handleParamsInUrl(targetReport, false);

        // Embedded/detail reports (showNoFilters) can't surface the filter panel and
        // the parent already set the scope, so run regardless of the filter tally and
        // let the backend validate — otherwise RunReport never fires and no data shows.
        if (!showNoFilters && filtersFilled < requiredFilters) {

            setState({ loadingReport: false, show: true });
            return;

        }

        const runReq = {

            // Embedded reports run with whatever the parent row supplied; the backend
            // NULL-binds anything still missing instead of rejecting the run.
            AllowMissingFilters: showNoFilters,
            CultureId: cultureId,
            Filters: targetReport.ReportFilters
                .filter(f => f.Value !== undefined && f.Value !== null && f.Value !== '')
                .map(f => ({ Id: f.Id, Value: f.Value })),
            ReportId: targetReport.Id,
            ScopeObjectId: scopeObjectId
        };

        try {

            setState({ loadingReport: true });
            const result = await ReportApi.RunReport(runReq);
            if (result) {

                setState({ report: result });

            } else {

                setState({ report: { ...targetReport, HasResults: false } as Report });

            }

        } catch (error: any) {

            console.error('Failed to run report:', error);
            const backendMessage = typeof error?.Message === 'string' ? error.Message : typeof error?.message === 'string' ? error.message : '';
            if (backendMessage.includes('expects parameter')) {

                createToast(formatMessage({ id: 'required_parameter_missing.49775350', defaultMessage: 'Required parameter missing' }), 'warning');

            } else if (backendMessage.includes('Required Filter')) {

                createToast(backendMessage, 'warning');

            } else {

                createToast(formatMessage({ id: 'unable_to_run_report.125072a9', defaultMessage: 'Unable to run Report' }), 'danger');

            }
            setState({ report: { ...targetReport, HasResults: false } as Report });

        } finally {

            setState({ loadingReport: false, runOnce: false });

        }

    };

    // ----------------------------------------------
    //  Report data helpers
    // ----------------------------------------------

    const setCustomFilterValue = (r: Report, filter: CustomFilter) => {

        const index = r.ReportFilters.findIndex(f => f.Id === filter.filterId);
        if (index !== -1) r.ReportFilters[index].Value = filter.valueId.toString();

    };

    // ----------------------------------------------
    //  Effects
    // ----------------------------------------------

    useEffect(() => {

        if (scopeObjectId) loadReports();

    }, [scopeObjectId, reportId]);

    // The Freshworks support widget is fixed to the bottom-right of the viewport
    // (see Default.aspx) and overlaps the offcanvas action buttons — most notably
    // "Run Report". Hide the launcher while the filter panel is open so those
    // buttons stay clickable, then restore it when the panel closes or unmounts.
    useEffect(() => {

        const widget = (window as Window & { FreshworksWidget?: (...args: unknown[]) => void }).FreshworksWidget;
        if (typeof widget !== 'function' || !show) return;

        widget('hide', 'launcher');
        return () => widget('show', 'launcher');

    }, [show]);

    // ----------------------------------------------
    //  Report component mapper
    // ----------------------------------------------

    const customSections = (r: Report) => r.ReportSections.filter(s => s.Custom);
    const showModeFilter = report && REPORTS_WITH_MODE_FILTER.includes(report.Id);

    const getReportJSX = (r: Report) => {

        switch (r.Id) {

            case 33: return <Report33 reportSections={customSections(r)} />;
            case 60: return <Report60 reportSections={customSections(r)} studentProfileId={studentProfileId} />;
            case 62: return <Report62 reportSections={customSections(r)} />;
            case 67: return <Report67 reportSections={customSections(r)} />;
            case 68: return <Report68 reportSections={customSections(r)} />;
            case 69: return <Report69 reportSections={customSections(r)} />;
            case 70: return <Report70 reportSections={customSections(r)} />;
            case 71: return <Report71 mode={mode} reportSections={customSections(r)} />;
            case 72: return <Report72 mode={mode} reportSections={customSections(r)} />;
            case 73: return <Report73 mode={mode} reportSections={customSections(r)} />;
            case 74: return <Report74 mode={mode} reportSections={customSections(r)} />;
            case 75: return <Report75 onRefresh={() => runActualReport(true)} reportSections={r.ReportSections.filter(s => !s.Custom)} />;
            default: return (
                <ComponentWrapper
                    hasResults={r.HasResults}
                    isSubReport={isSubReport}
                    loading={loadingReport}
                    reportSections={r.ReportSections.filter(s => !s.Custom)}
                />
            );

        }

    };

    // ----------------------------------------------
    //  Filter display (summary bar)
    // ----------------------------------------------

    const getFilterDisplay = (filter: ReportFilter): { display: string; tooltip: string } => {

        if (filter.Value === undefined) return { display: EMPTY_DISPLAY, tooltip: '' };

        const values = filter.Value.split(',').map(value => value.trim()).filter(Boolean);
        const count = values.length;
        let listText: string;
        switch (filter.FieldType) {

            case 'DatePicker': {

                const mediumDateOpts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
                const dates = filter.Value.split('|').map(d => d.trim());
                if (dates.length !== 2 || isNaN(new Date(dates[0]).getTime()) || isNaN(new Date(dates[1]).getTime())) {

                    listText = `Since Start - ${intl.formatDate(new Date(), mediumDateOpts)}`;
                    break;

                }
                const startDate = new Date(dates[0]);
                const today = new Date();
                const daysDiff = Math.floor((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
                const isSinceStart = daysDiff >= SINCE_START_THRESHOLD_DAYS;

                listText = dates.map((d, i) => i === 0 && isSinceStart
                    ? 'Since Start'
                    : intl.formatDate(new Date(d), mediumDateOpts)
                ).join(' - ') || EMPTY_DISPLAY;
                break;

            }
            case 'StudentGroupsSearch':
                listText = values.map(id => currentClassroomList.find(s => s.Id === Number(id))?.Name).filter(Boolean).join(', ') || EMPTY_DISPLAY;
                break;
            case 'TransactionSearch':
                listText = values.map(id => currentAccessCodeList.find(t => t.Id === Number(id))?.Name).filter(Boolean).join(', ') || EMPTY_DISPLAY;
                break;
            case 'StudentSearch':
                listText = values.map(id => {

                    const student = currentStudentList.find(s => s.Id === Number(id));
                    return student
                        ? (student.Fullname || `${student.Firstname} ${student.Lastname}`.trim() || id)
                        : id;

                }).join(', ') || EMPTY_DISPLAY;
                break;
            case 'PartnerSearch':
                listText = values.map(id => currentPartnerList.find(p => p.Id === Number(id))?.Name).filter(Boolean).join(', ') || EMPTY_DISPLAY;
                break;
            case 'AssignmentSearch':
                listText = filter.Value || EMPTY_DISPLAY;
                break;
            case 'CheckBox':
                listText = filter.Value === 'true' ? 'Yes' : filter.Value === 'false' ? 'No' : EMPTY_DISPLAY;
                break;
            case 'DropDownList': {

                const DROPDOWN_FIELD_MAP: Record<string, { dataItemKey: keyof Option; textField: keyof Option }> = {
                    ClassroomProducts: { dataItemKey: 'ProductId', textField: 'ProductName' },
                    ClassroomTests: { dataItemKey: 'Value', textField: 'Text' }
                };
                const { dataItemKey, textField } = DROPDOWN_FIELD_MAP[filter.Name] ?? { dataItemKey: 'Value' as keyof Option, textField: 'Text' as keyof Option };

                const selectedValues = values.filter(value => value !== '0');
                listText = selectedValues
                    .map(value => {

                        const option = filter.FilterOptions?.find(o => o[dataItemKey]?.toLowerCase() === value?.toLowerCase()
                        );
                        return option?.[textField] || option?.Text || value;

                    })
                    .join(', ') || EMPTY_DISPLAY;
                break;

            }
            default:
                listText = filter.Value;

        }

        if (count > 1) {

            const label = MULTI_VALUE_LABELS[filter.FieldType] ?? 'items';
            return { display: `${count} ${label} selected`, tooltip: listText };

        }

        return { display: listText || EMPTY_DISPLAY, tooltip: listText };

    };

    // ----------------------------------------------
    //  Render helpers
    // ----------------------------------------------

    // `showNoFilters` locks an embedded report: no filter bar, no filter offcanvas.
    const showFilterBar = report && report.Id !== 60 && !showNoFilters;

    const renderFilterSummaryItem = (filter: ReportFilter, index: number) => {

        const { display, tooltip } = getFilterDisplay(filter);

        return (
            <Col className="generate-report__filter-col" key={`${index}_filter`}>
                {tooltip ? (
                    <ReportToolTip tooltip={tooltip} trigger="hover">
                        <FormLabel className="generate-report__filter-label">
                            {filter.DisplayName || filter.Name}
                        </FormLabel>
                    </ReportToolTip>
                ) : (
                    <FormLabel className="generate-report__filter-label">
                        {filter.DisplayName || filter.Name}
                    </FormLabel>
                )}
                <p className="generate-report__filter-value">
                    {display}
                </p>
            </Col>
        );

    };

    const getReportSkeleton = (): React.ReactElement | null => {

        switch (reportId) {

            case 67: return <Report67Skeleton />;
            case 68: return <Report68Skeleton />;
            case 69: return <Report69Skeleton />;
            case 70: return <Report70Skeleton />;
            case 71: return <Report71Skeleton />;
            case 72: return <Report72Skeleton />;
            case 73: return <Report73Skeleton />;
            case 74: return <Report74Skeleton />;
            default: return null;

        }

    };

    const renderReportContent = () => {

        if (loadingReport) {

            const skeleton = getReportSkeleton();
            if (skeleton) return skeleton;

            return (
                <Container className="generate-report__loader">
                    <GMetrixLoader />
                </Container>
            );

        }

        if (testTypeId === '12') {

            return (
                <KaplanReport resultId={scopeObjectId} studentProfileId={studentProfileId} />
            );

        }

        if (!report?.HasResults) {

            return (
                <Container fluid className="generate-report__empty-state">
                    <FormattedMessage defaultMessage="No Results Found - Adjust your filters to see results" id="no_results_found_adjust_your_filters_to.90b0a8f0" />
                </Container>
            );

        }
        return getReportJSX(report);

    };

    // ----------------------------------------------
    //  Render
    // ----------------------------------------------

    return (
        <div className="generate-report">
            {showFilterBar && (
                <>
                    <Card className="generate-report__filter-bar">
                        <Container fluid className="px-0">
                            <Row className="align-items-start">
                                <Col xs="auto">
                                    <ReportToolTip tooltip={formatMessage({ id: 'set_filters_to_run_this_report.db241bf1', defaultMessage: 'Set filters to run this report' })} trigger="hover">
                                        <GMetrixButton iconClass="fa-regular fa-filter" onClick={handleShow} variant="primary">
                                            <FormattedMessage defaultMessage="Report Filters" id="report_filters.8aa9450b" />
                                        </GMetrixButton>
                                    </ReportToolTip>
                                </Col>
                                {reportFilters?.filter(f => f.DisplayName !== 'Culture').map(renderFilterSummaryItem)}
                                {showModeFilter && (
                                    <Col className="generate-report__filter-col">
                                        <FormLabel className="generate-report__filter-label">
                                            <FormattedMessage defaultMessage="Mode" id="mode.f65b1f2b" />
                                        </FormLabel>
                                        <p className="generate-report__filter-value">
                                            {mode}
                                        </p>
                                    </Col>
                                )}
                            </Row>
                        </Container>
                    </Card>

                    <Offcanvas scroll backdrop={show} onHide={handleClose} placement="end" show={show}>
                        <Offcanvas.Header closeButton className="generate-report__offcanvas-header">
                            <Offcanvas.Title>
                                <FormattedMessage defaultMessage="Report Filters" id="report_filters.8aa9450b" />
                            </Offcanvas.Title>
                        </Offcanvas.Header>
                        <Offcanvas.Body>
                            <FilterWrapper
                                handleClose={handleClose}
                                handleShow={handleShow}
                                mode={mode}
                                onAccessCodeDataLoaded={data => setState({ currentAccessCodeList: data })}
                                onClassroomDataLoaded={data => setState({ currentClassroomList: data })}
                                onPartnerDataLoaded={data => setState({ currentPartnerList: data })}
                                onStudentDataLoaded={data => setState({ currentStudentList: data })}
                                removeFilters={removeFilters}
                                report={report}
                                reportFilters={reportFilters}
                                runActualReport={runActualReport}
                                scopeId={scopeId}
                                scopeObjectId={scopeObjectId}
                                setLoadingReport={handleSetLoadingReport}
                                setMode={showModeFilter ? handleSetMode : undefined}
                                setReport={handleSetReport}
                                setReportFilters={handleSetReportFilters}
                                showNoFilters={showNoFilters}
                                showStartEndDateOnly={showStartEndDateOnly}
                                urlSync={urlSync}
                            />
                        </Offcanvas.Body>
                    </Offcanvas>
                </>
            )}

            {renderReportContent()}
        </div>
    );

}
